import { ChatProvider, useChatStore } from '~/training/chat/Store';
import { Overlay } from '~/ui/overlay/Overlay';
import { Flex } from '~/ui/flex/Flex';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { createPortal } from 'react-dom';
import styled from '@xstyled/styled-components';
import { ChatGptButton } from '~/ui/buttons';
import { withTransition } from '~/ui/transition/Transition';
import { Card } from '~/ui/card/Card';
import { CloseButton } from '~/ui/buttons/CloseButton';
import Icon from '~/assets/icons/MagicLlama.svg';
import SendIcon from '~/assets/icons/sendMessage.svg';
import MicrophoneIcon from '~/assets/icons/microphone.svg';
import SpinnerIcon from '~/assets/icons/chatSpinner.svg';
import { processChatBotMessage } from '~/training/chat/query/sendMessage';
import { useTrainingParams } from '~/training/useTrainingParams';
import { TrainingState, useTrainingStore } from '~/training/state';
import { ChatBotMessage } from '~/training/state/chatBot/ChatBotSlice';
import Pusher, { Channel } from 'pusher-js';
import Markdown from 'markdown-to-jsx';
import { t } from 'ttag';

interface SpeechRecognitionResult {
  transcript: string;
}

interface SpeechRecognitionResultList {
  [index: number]: { [index: number]: SpeechRecognitionResult };
}

interface SpeechRecognitionEventData {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance {
  onresult: ((event: SpeechRecognitionEventData) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionWindow extends Window {
  SpeechRecognition?: new () => SpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
}

const selector = (s: MediaPlayerStore) => select(s, 'pause');
const selectorTraining = (s: TrainingState) =>
  select(s, 'chatMessages', 'chatGreeting', 'chatQuestions');

type MessageData = ChatBotMessage;

const ChatBotForm = styled.div`
  height: calc(100% - 38px);
  display: flex;
  flex-direction: column;

  .chat {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid var(--Grey-E0, #e0e0e0);

    .message {
      display: table;
      padding: 4px 10px 4px 10px;
      margin: 5px;
      color: #fff;
      font-size: 14px;
      font-family: Raleway;
      margin-bottom: 12px;
    }

    .incoming {
      background: #f3ecff;
      color: #000;
      border-radius: 20px 20px 20px 0px;
      margin-right: 15px;
    }

    .outgoing {
      background: #9738ff;
      color: #fff;
      margin-left: auto;
      border-radius: 20px 20px 0px 20px;
    }

    .btnSection {
      margin-bottom: 10px;
      button {
        border-radius: 4px;
        border: 1px solid var(--Grey-E0, #e0e0e0);
        color: var(--Black-00, #000);
        font-variant-numeric: lining-nums proportional-nums;
        font-family: Raleway;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        background-color: #fff;
        padding: 10px;
        width: 100%;
        margin-top: 8px;
      }
    }

    .firstScreen {
      display: flex;
      flex-direction: column;
      height: 100%;

      .flex {
        flex: 1;
        .container {
          display: flex;
          height: 100%;
          justify-content: center;
          align-content: center;
          flex-wrap: wrap;
        }
      }
    }
  }

  .form {
    padding-top: 10px;
    display: flex;
    flex-direction: row;

    .mess {
      border-radius: 5px;
      background: var(--Light-F5, #f5f5f5);
      padding-left: 10px;
      font-variant-numeric: lining-nums proportional-nums;
      border: 0px;
      flex: 1;
      padding-top: 10px;
      padding-bottom: 10px;

      font-family: Raleway;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      color: var(--Grey-6F, #6f6f6f);
    }

    button {
      padding: 6px;
      margin-left: 10px;
      background-color: var(--color-accent-dk);
      color: white;
      border: none;
      cursor: pointer;

      border-radius: 8px;
    }

    @keyframes pulseAnimation {
      0% {
        opacity: 1;
        background-color: var(--color-accent-dk);
      }
      50% {
        opacity: 0.8;
        background-color: blue;
      }
      100% {
        opacity: 1;
        background-color: var(--color-accent-dk);
      }
    }

    .recording {
      animation: pulseAnimation 2s infinite;
    }
  }
`;

const Tooltip = styled.div<{ show: boolean }>`
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  background-color: var(--color-black);
  color: var(--color-white);
  text-align: center;
  border-radius: 5px;
  padding: 5px 10px;
  position: absolute;
  z-index: 1;
  transform: translateX(50%);
  white-space: nowrap;

  font-size: 14px;
  right: 20px;
  margin-top: 50px;

  &::after {
    content: '';
    position: absolute;
    top: -15%;
    right: 20px;
    margin-top: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent transparent var(--color-black);
    transform: rotate(270deg);
  }
`;

const ButtonWrapper = styled.div`
  position: fixed;
  top: 170px;
  right: 90px;
  font-size: 30px;
  z-index: 1;
  padding: 0px;

  @media ((max-width: 580px) and (max-height: 300px)) {
    right: 10px;
  }

  &:hover ${Tooltip} {
    visibility: visible;
  }
`;

interface TooltipButtonProps {
  tooltipText: string;
}

const TooltipButton: React.FC<TooltipButtonProps> = ({ tooltipText }) => {
  const { toggle } = useChatStore();
  const { pause } = useMediaPlayerStore(selector);
  const storedData = localStorage.getItem('hideTooltipForChatBot');
  const [showTooltip, setShowTooltip] = useState(!storedData);

  return (
    <ButtonWrapper>
      <Button
        onClick={() => {
          pause();
          toggle();
          localStorage.setItem('hideTooltipForChatBot', 'true');
          setShowTooltip(false);
        }}
      />
      <Tooltip show={showTooltip}>{tooltipText}</Tooltip>
    </ButtonWrapper>
  );
};

const Button = styled(ChatGptButton)`
  position: fixed;
  top: 170px;
  right: 20px;
  font-size: 30px;
  z-index: hud;
  padding: 0px;
  @media ((max-width: 580px) and (max-height: 300px)) {
    right: 10px;
  }
`;

const ChatBotHeader = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;

  svg {
    margin-top: -6px;
    margin-right: 10px;
  }

  b {
    font-family: var(--title-font);
    font-size: 24px;
  }
`;

const OpenChatButton = () => {
  return createPortal(
    <>
      <TooltipButton
        tooltipText={t`Try out our new AI Assistant`}
      >{t`Try out our new AI Assistant`}</TooltipButton>
    </>,
    document.getElementById('root')!
  );
};

const ChatBotCard = withTransition(Card);

const ChatBotUI = () => {
  const { open, toggle } = useChatStore();
  useOpenCloseAudioEffect(open);

  const { learnerTrainingId } = useTrainingParams();
  const { chatMessages, chatGreeting, chatQuestions } =
    useTrainingStore(selectorTraining);

  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<MessageData[]>(chatMessages || []);
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);

  const escFunction = useCallback(event => {
    if (event.keyCode === 27) {
      toggle(false);
    }
  }, []);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => scrollToBottom(), [open]);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);
    return () => document.removeEventListener('keydown', escFunction);
  }, [escFunction]);

  let pusherInstance: Pusher | null = null;
  let channelInstance: Channel | null = null;

  const SendMessageToOpenAi = (text: string) => {
    if (!text || text.trim().length < 1) {
      return false;
    }

    setLoading(true);
    setTimeout(() => scrollToBottom(), 0);

    setMessages([
      ...messages,
      {
        id: '',
        body: text,
        role: 'user',
        ai_message_id: '0',
        chatbot_thread_id: 0,
        created_at: '',
        updated_at: ''
      }
    ]);

    if (pusherInstance && channelInstance) {
      channelInstance?.unbind('chatbot-message-updated');
      pusherInstance?.unsubscribe(channelInstance.name);
    }

    const pathSegments = window.location.pathname.split('/');
    let token = sessionStorage.getItem('token');

    if (!token) {
      token = pathSegments && pathSegments[2] ? pathSegments[2] : '';
    }

    pusherInstance = new Pusher('28135426a93eba52ad52', {
      cluster: 'us2',
      authEndpoint: `${process.env.REACT_APP_BACKEND_API_BASE_URL}/pusher/learner-auth`,
      authTransport: 'ajax',
      auth: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });

    pusherInstance.connection.bind('error', (err: unknown) => {
      if (
        (err as { error: { data: { code: number } } }).error.data.code === 4004
      ) {
        console.log('Over limit!');
      } else {
        console.log('Pusher error: ', err);
      }
      setLoading(false);
    });

    processChatBotMessage({
      learnerTrainingUuid: learnerTrainingId,
      message: text
    }).then(channelName => {
      if (channelName && pusherInstance) {
        channelInstance = pusherInstance.subscribe(channelName);

        channelInstance.bind('chatbot-message-updated', (data: MessageData) => {
          setMessages(prevMessages => {
            const messageIndex = prevMessages.findIndex(
              msg => msg.id === data.id
            );

            if (messageIndex !== -1) {
              const updatedMessages = [...prevMessages];
              updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                ...data
              };
              return updatedMessages;
            } else {
              return [...prevMessages, data];
            }
          });

          if (data?.body) {
            setMessage('');
            setLoading(false);
            scrollToBottom();
          }
        });
      }
    });
  };

  useEffect(() => {
    const speechWindow = window as SpeechRecognitionWindow;
    const recognitionAvailable =
      'SpeechRecognition' in window ||
      ('webkitSpeechRecognition' in window &&
        speechWindow.webkitSpeechRecognition);

    if (recognitionAvailable) {
      setIsSupported(true);
    } else {
      setIsSupported(false);
      // Silently ignore unsupported browser features
    }
  }, []);

  const handleStartRecognition = () => {
    const speechWindow = window as SpeechRecognitionWindow;
    const SpeechRecognitionClass =
      speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionClass!();

    recognition.onresult = (event: SpeechRecognitionEventData) => {
      const transcript = event.results[0][0].transcript;
      setMessage(transcript);
    };

    recognition.onend = () => {
      recognition.stop();
      setIsRecording(false);
    };

    try {
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      // Silently ignore speech recognition errors
      setIsRecording(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      SendMessageToOpenAi(message);
    }
  };

  return (
    <>
      <OpenChatButton />
      <Overlay open={open} onClick={() => toggle(false)}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'stretch', sm: 'flex-end' }}
          alignItems={{ _: 'flex-start', sm: 'flex-start' }}
          overflow="hidden"
          aria-modal={open}
        >
          <ChatBotCard
            show={open}
            w={{ _: '100%', sm: '353px' }}
            h={{ _: '85vh', sm: '550px' }}
          >
            <CloseButton onClick={() => toggle(false)} />
            <Flex variant="list" className="modalStyleForDarkTheme">
              <ChatBotHeader>
                <Icon />
              </ChatBotHeader>
              <ChatBotForm>
                <div className="chat" ref={chatContainerRef}>
                  <div className="firstScreen">
                    <div className="flex">
                      <div className="container"></div>
                    </div>
                    <div className="message incoming">
                      <p>{chatGreeting}</p>
                      <div className="btnSection">
                        {chatQuestions && chatQuestions.length
                          ? chatQuestions.map((q, i: number) => {
                              return (
                                <button
                                  key={`q_${i}`}
                                  onClick={() => SendMessageToOpenAi(q)}
                                >
                                  {q}
                                </button>
                              );
                            })
                          : false}
                      </div>
                    </div>
                  </div>

                  {messages && messages.length
                    ? messages.map((m, i: number) => {
                        return (
                          <span
                            key={`m_${i}`}
                            className={`message ${
                              m.role == 'user' ? 'outgoing' : 'incoming'
                            }`}
                          >
                            <Markdown>{m?.body || ''}</Markdown>
                          </span>
                        );
                      })
                    : false}
                  {loading && <SpinnerIcon style={{ width: '50px' }} />}
                </div>
                <div className="form">
                  <input
                    type="text"
                    placeholder={t`Type here`}
                    className="mess"
                    value={message}
                    disabled={loading}
                    onChange={e => {
                      setMessage(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                  />
                  {isSupported && (
                    <button
                      onClick={handleStartRecognition}
                      disabled={loading}
                      className={isRecording ? 'recording' : ''}
                    >
                      <MicrophoneIcon />
                    </button>
                  )}
                  <button
                    onClick={() => SendMessageToOpenAi(message)}
                    disabled={loading}
                  >
                    <SendIcon />
                  </button>
                </div>
              </ChatBotForm>
            </Flex>
          </ChatBotCard>
        </Flex>
      </Overlay>
    </>
  );
};

export const Chat = () => {
  return (
    <ChatProvider>
      <ChatBotUI />
    </ChatProvider>
  );
};
